/**
 * Which of a realtor's follow-ups the bell has already shown them.
 *
 * Follow-ups are computed on read, so they have no server-side read state.
 * "Seen" is a per-browser, per-user set of `contactId:leadingReason` keys: the
 * bell badge counts the people not in it. Once the follow-ups have loaded,
 * opening the bell marks them seen, and so does clicking the panel's
 * follow-ups row (which also marks anyone who joined the queue while the
 * panel was open). If the first load failed or is still loading, nothing is
 * marked and the stored set is kept; if a later refresh fails, the last
 * loaded list keeps being shown and used (`isError` is only reported while
 * there is no list). A person whose leading reason changes has a new key, so
 * they count as unseen again. Keys for people who have left the queue are
 * dropped, so someone who returns is unseen again too.
 *
 * The seen set is stored per user in localStorage (shared by the tabs of this
 * browser); each mounted instance keeps its own in-memory copy. There is no
 * live sync between tabs, but a write merges: it reads the stored set, unions
 * it with this instance's copy (and, on opening the bell, the current queue),
 * keeps only people still in the queue, and stores that, so keys another tab
 * saved for people still in the queue are not lost.
 *
 * Caller precondition: the user is a realtor. The list endpoint is behind
 * `requireUserType("realtor")`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { useFollowUps } from "@/features/realtor/hooks/use-realtor-crm";
import type { FollowUpReasonType } from "@/features/realtor/services/realtor-crm-api.service";

const STORAGE_PREFIX = "finishd:follow-ups-seen:";

type FollowUpKey = `${string}:${FollowUpReasonType}`;

/** Exhaustive over the union, so a new reason is a compile error here. */
const REASON_TYPES: Record<FollowUpReasonType, true> = {
	reply_waiting: true,
	reminder_due: true,
	viewing_feedback: true,
	gone_quiet: true,
	listing_match: true,
};

/**
 * One person, one leading reason. The key is unambiguous because the reasons
 * are a closed union of literals that contain no colon, and a contact id is a
 * uuid, which contains none either.
 */
function followUpKey(
	contactId: string,
	reason: FollowUpReasonType,
): FollowUpKey {
	return `${contactId}:${reason}`;
}

function isFollowUpKey(value: unknown): value is FollowUpKey {
	if (typeof value !== "string") return false;
	const colon = value.lastIndexOf(":");
	return (
		colon > 0 && Object.keys(REASON_TYPES).includes(value.slice(colon + 1))
	);
}

let warnedStorageUnavailable = false;
let warnedSaveFailed = false;

function readSeen(storageKey: string): Set<FollowUpKey> {
	let raw: string | null;
	try {
		raw = window.localStorage.getItem(storageKey);
	} catch {
		// Storage is unavailable (blocked, or a SecurityError): nothing can be
		// remembered, so nothing is seen. Say so once rather than on every read.
		if (!warnedStorageUnavailable) {
			warnedStorageUnavailable = true;
			console.warn(
				"Follow-ups: browser storage is unavailable, so seen follow-ups will not be remembered.",
			);
		}
		return new Set();
	}
	if (!raw) return new Set();
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		// Unparseable JSON is a corrupt value: start empty, quietly.
		return new Set();
	}
	// A non-array value is corrupt too; so are non-key entries inside an array.
	return new Set(Array.isArray(parsed) ? parsed.filter(isFollowUpKey) : []);
}

function writeSeen(storageKey: string, seen: Set<FollowUpKey>): void {
	try {
		window.localStorage.setItem(storageKey, JSON.stringify([...seen]));
	} catch (error) {
		// QuotaExceededError or SecurityError: the badge resets on reload. Say so
		// once rather than on every write.
		if (!warnedSaveFailed) {
			warnedSaveFailed = true;
			console.warn(
				"Follow-ups: could not save which follow-ups were seen.",
				error,
			);
		}
	}
}

/**
 * The stored set (which another tab may have changed) joined with this
 * instance's copy and `extra`, minus anyone no longer in the queue.
 */
function mergeSeen(
	persisted: Iterable<FollowUpKey>,
	memory: Iterable<FollowUpKey>,
	extra: readonly FollowUpKey[],
	queue: ReadonlySet<FollowUpKey>,
): Set<FollowUpKey> {
	const merged = new Set([...persisted, ...memory, ...extra]);
	for (const key of merged) if (!queue.has(key)) merged.delete(key);
	return merged;
}

function sameKeys(a: ReadonlySet<FollowUpKey>, b: ReadonlySet<FollowUpKey>) {
	return a.size === b.size && [...a].every((k) => b.has(k));
}

export interface FollowUpsSeen {
	/** People in the queue (one per contact). */
	total: number;
	/** People in the queue the realtor has not yet seen. */
	unseenCount: number;
	/** Marks everyone currently in the queue seen, once the list has loaded. */
	markAllSeen: () => void;
	/** True only while the first load has failed and there is no list. */
	isError: boolean;
	/** Set exactly when `isError` is true. */
	error: Error | null;
	isFetching: boolean;
	refetch: () => void;
}

/** Realtor-only: it fetches the follow-up list. */
export function useFollowUpsSeen(): FollowUpsSeen {
	const userId = useAuthStore((s) => s.user?.id ?? "anonymous");
	const storageKey = `${STORAGE_PREFIX}${userId}`;
	const list = useFollowUps();
	// The set is stored with the key it was read for, so a render right after
	// a user switch never mixes one user's set with another's storage.
	const [stored, setStored] = useState(() => ({
		storageKey,
		seen: readSeen(storageKey),
	}));

	useEffect(() => {
		setStored((current) =>
			current.storageKey === storageKey
				? current
				: { storageKey, seen: readSeen(storageKey) },
		);
	}, [storageKey]);

	const seen = useMemo(
		() =>
			stored.storageKey === storageKey ? stored.seen : readSeen(storageKey),
		[stored, storageKey],
	);

	// One key per person, however many entries the list holds for them.
	const keys = useMemo(() => {
		const byContact = new Map<string, FollowUpKey>();
		for (const entry of list.data?.followUps ?? []) {
			if (!byContact.has(entry.contact.id)) {
				byContact.set(
					entry.contact.id,
					followUpKey(entry.contact.id, entry.leadingReason),
				);
			}
		}
		return [...byContact.values()];
	}, [list.data]);

	// Forget people who have left the queue, so a return is unseen again.
	useEffect(() => {
		if (!list.data || stored.storageKey !== storageKey) return;
		const persisted = readSeen(storageKey);
		const next = mergeSeen(persisted, stored.seen, [], new Set(keys));
		// Only a real change sets state (a failed write must not re-trigger this).
		if (!sameKeys(next, stored.seen)) setStored({ storageKey, seen: next });
		if (!sameKeys(next, persisted)) writeSeen(storageKey, next);
	}, [list.data, keys, stored, storageKey]);

	const markAllSeen = useCallback(() => {
		// No list yet, or it failed: there is nothing to mark, and replacing the
		// stored set with an empty one would make everyone unseen again.
		if (!list.data) return;
		const next = mergeSeen(readSeen(storageKey), seen, keys, new Set(keys));
		setStored({ storageKey, seen: next });
		writeSeen(storageKey, next);
	}, [list.data, keys, seen, storageKey]);

	// A failed refresh keeps the last list, which stays in use.
	const failedWithoutList = list.isError && !list.data;

	return {
		total: keys.length,
		unseenCount: keys.filter((k) => !seen.has(k)).length,
		markAllSeen,
		isError: failedWithoutList,
		error: failedWithoutList ? list.error : null,
		isFetching: list.isFetching,
		refetch: () => void list.refetch(),
	};
}
